<template>
  <el-dialog
    :visible="visible"
    title="自动化采购报告"
    class="dialog"
    :close-on-click-modal="false"
    :width="useEditor || usePreview ? '900px' : '30rem'"
    @close="closeDialog"
  >
    <div class="chart-wrap">
      <div ref="bidMethodChart" class="chart"></div>
      <div ref="projectTypeChart" class="chart"></div>
    </div>
    <div v-if="useEditor" class="editor-wrap">
      <u-editor
        ref="editor"
        v-model="wordHtml"
        :show-choose-template="true"
        label-width="110px"
        :config="editorConfig"
      />
      <div class="flex row-right m-t-24">
        <el-button @click="reCreateReport">重新生成报告</el-button>
        <el-button @click="closeDialog">取消</el-button>
        <el-button type="primary" @click="save">保存</el-button>
      </div>
    </div>
    <div
      v-else-if="usePreview"
      v-loading="loading"
      element-loading-text="正在生成报告，请勿离开"
      element-loading-spinner="el-icon-loading"
    >
      <div
        :id="`canvas-wrap-${_uid}`"
        v-infinite-scroll="renderPageStep"
        infinite-scroll-immediate="false"
        infinite-scroll-distance="100"
        class="text-center"
      ></div>
    </div>
    <div
      v-else
      v-loading="loading"
      element-loading-text="正在生成报告，请勿离开"
      element-loading-spinner="el-icon-loading"
      class="loading"
    ></div>
  </el-dialog>
</template>

<script>
import echarts from 'echarts'
import {
  getJxicBodyHtml,
  getJxicWordHtml,
  getJxicWordHtmlByConfig
} from '@/components/procurement-report/jxic-report'
import UEditor from '@/components/common/UEditor/index.vue'
import he from 'he'
import { downloadHtmlToPdfApi } from '@/api/up-file'
import * as PDFJS from 'pdfjs-dist'
import {
  changeJxicPurchaseReportApi,
  getJxicPurchaseReportApi
} from '@/api/jxic-document'
import { getBase64FromImgUrl } from '@/utils/utils'
import jxicIconImage from '@/assets/images/query-stats/jxic-icon.png'
PDFJS.GlobalWorkerOptions.workerSrc = '/pdfjs/build/pdf.worker.js'

/**
 * 省投自动化报告生成弹窗
 */
export default {
  name: 'JxicReportDialog',
  components: { UEditor },

  props: {
    useEditor: {
      type: Boolean,
      default: false
    },

    usePreview: {
      type: Boolean,
      default: false
    },

    reportId: {
      type: [String, Number],
      default: ''
    }
  },

  data() {
    return {
      visible: false,
      bidMethodSrc: '',
      projectTypeSrc: '',
      wordHtml: '',
      editorConfig: {
        allowDivTransToP: false
      },

      loading: false,

      fileUrl: '',

      // pdf的父元素
      canvasWrapEle: null,

      // pdf文件变量
      pdfPages: 0, // pdf的总页数
      pdfWidth: 776, // pdf宽度
      pdfDoc: '', // 文档内容
      pdfScale: 1.0, // 放大倍数

      // 异步渲染变量
      currentPage: 0, // 当前渲染到的页数
      renderingNum: 0, // 目前正在渲染的页面数量

      // 每次触发渲染的页面数量
      renderNum: 10,

      reportInfo: {}
    }
  },

  watch: {
    visible(val) {
      if (val) {
        this.loading = true
        this.$nextTick(() => this.drawChart())
      }
    }
  },

  methods: {
    closeDialog() {
      this.$emit('cancel')
      this.wordHtml = ''
    },

    reCreateReport() {
      this.$systemConfirm(
        '系统将重新生成报告，已编辑的内容不会保存，是否确认？',
        '系统提示',
        '确定',
        '取消',
        4999
      ).then(result => {
        if (!result) return

        this.drawBidMethodChart()
        this.drawProjectTypeChart()
        getBase64FromImgUrl(jxicIconImage).then(icon => {
          const wordBodyConfig = {
            bidMethodSrc: this.bidMethodSrc,
            projectTypeSrc: this.projectTypeSrc,
            reportVariables: this.reportInfo.reportVariables || [],
            jxicIcon: icon
          }
          this.wordHtml = he.decode(getJxicBodyHtml(wordBodyConfig), {
            allowUnsafeSymbols: true
          })
        })
      })
    },

    drawChart() {
      this.getReport().then(() => {
        this.drawBidMethodChart()
        this.drawProjectTypeChart()
        getBase64FromImgUrl(jxicIconImage).then(icon => {
          const wordBodyConfig = {
            bidMethodSrc: this.bidMethodSrc,
            projectTypeSrc: this.projectTypeSrc,
            reportVariables: this.reportInfo.reportVariables || [],
            jxicIcon: icon
          }
          if (this.useEditor) {
            this.wordHtml = this.wordHtml
              ? this.wordHtml
              : he.decode(getJxicBodyHtml(wordBodyConfig), {
                allowUnsafeSymbols: true
              })
          } else {
            this.wordHtml = this.wordHtml
              ? getJxicWordHtml(this.wordHtml).replaceAll('&nbsp;', '')
              : getJxicWordHtmlByConfig(wordBodyConfig)
            this.getWordPdf()
          }
        })
      })
    },

    getReport() {
      return getJxicPurchaseReportApi(this.reportId)
        .then(res => {
          this.reportInfo = res.data?.reportInfo || {}
          this.wordHtml = this.reportInfo.reportContent || ''
        })
        .catch(err => {
          if (err?.message) this.$message.error(err.message)
        })
    },

    drawBidMethodChart() {
      const ordersByPurchaseType = (this.reportInfo.reportVariables || []).find(
        item => item.name === '按采购类型统计项目列表'
      )?.value
      const ordersList = ordersByPurchaseType.map(item => {
        return {
          name: item.find(config => config.name === '采购类型名称')?.value,
          value: item.find(config => config.name === '占比')?.value
        }
      })
      const chartInstance = echarts.init(this.$refs.bidMethodChart)
      chartInstance.setOption({
        color: [
          '#4786FF',
          '#14C7C7',
          '#FFB266',
          '#8B8AE6',
          '#49B0F5',
          '#FFA299'
        ],
        series: [
          {
            name: 'Access From',
            type: 'pie',
            radius: ['40%', '70%'],
            label: {
              show: true,
              formatter(param) {
                // correct the percentage
                return param.name + ' ' + param.value + '%'
              },
              color: '#000'
            },
            data: ordersList,
            animation: false
          }
        ]
      })
      this.bidMethodSrc = chartInstance.getDataURL({
        type: 'png',
        pixelRatio: 2
      })
    },

    drawProjectTypeChart() {
      const ordersByProjectType = (this.reportInfo.reportVariables || []).find(
        item => item.name === '按项目类型统计项目'
      )?.value
      const ordersList = ordersByProjectType.map(item => {
        return {
          name: item.find(config => config.name === '项目类型名称').value,
          value: item.find(config => config.name === '占比').value
        }
      })
      const chartInstance = echarts.init(this.$refs.projectTypeChart)
      chartInstance.setOption({
        color: [
          '#4786FF',
          '#14C7C7',
          '#FFB266',
          '#8B8AE6',
          '#49B0F5',
          '#FFA299'
        ],
        series: [
          {
            name: 'Access From',
            type: 'pie',
            radius: ['40%', '70%'],
            label: {
              show: true,
              formatter(param) {
                // correct the percentage
                return param.name + ' ' + param.value + '%'
              },
              color: '#000'
            },
            data: ordersList,
            animation: false
          }
        ]
      })
      this.projectTypeSrc = chartInstance.getDataURL({
        type: 'png',
        pixelRatio: 2
      })
    },

    getWordPdf() {
      console.log('wordHtml', this.wordHtml)
      const fileName = this.reportInfo.reportName + '.pdf'
      const postData = {
        name: fileName,
        content: this.wordHtml,
        watermark: {
          watermarkType: 1,
          watermarkWord: '江西投资集团电子采购平台'
        }
      }
      if (this.usePreview) {
        this.previewPdf(fileName, postData)
        return
      }
      this.downloadPdf(fileName, postData)
    },

    async previewPdf(fileName, postData) {
      try {
        const res = await downloadHtmlToPdfApi(postData)
        this.canvasWrapEle = document.getElementById(`canvas-wrap-${this._uid}`)
        const pdfData = new Uint8Array(await res.arrayBuffer())
        const loadingTask = PDFJS.getDocument({ data: pdfData })
        const pdf = await loadingTask.promise
        this.pdfDoc = pdf
        this.pdfPages = pdf.numPages
        this.$nextTick(() => {
          this.renderPageStep()
        })
      } catch (err) {
        console.error('preview pdf error', err)
        if (err?.message) this.$message.error(err.message)
      }
    },

    /**
     * 无限滚动，分批渲染pdf
     */
    async renderPageStep() {
      // 渲染完成则不再渲染
      if (this.currentPage === this.pdfPages) return

      // 需等待一次循环完成后，再触发
      if (this.renderingNum > 0) return

      const leftPages = this.pdfPages - this.currentPage
      this.renderingNum =
        leftPages > this.renderNum ? this.renderNum : leftPages

      while (this.renderingNum) {
        try {
          await this.renderPage(this.currentPage + 1)
          this.renderingNum--
          this.currentPage++
        } catch (e) {
          this.renderingNum = 0
        } finally {
          this.loading = false
        }
      }
    },

    /**
     * 对pdf文件进行逐页渲染
     * @param num
     * @returns {*}
     */
    renderPage(num) {
      return this.pdfDoc.getPage(num).then(page => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        const dpr = window.devicePixelRatio || 1
        const bsr =
          ctx.webkitBackingStorePixelRatio ||
          ctx.mozBackingStorePixelRatio ||
          ctx.msBackingStorePixelRatio ||
          ctx.oBackingStorePixelRatio ||
          ctx.backingStorePixelRatio ||
          0.6
        const ratio = dpr / bsr
        const viewport = page.getViewport({ scale: this.pdfScale })
        canvas.width = viewport.width * ratio
        canvas.height = viewport.height * ratio
        canvas.style.cssText = `width: ${this.pdfWidth}px; height: auto`

        ctx.setTransform(ratio, 0, 0, ratio, 0, 0)
        // 将 PDF 页面渲染到 canvas 上下文中
        const renderContext = {
          canvasContext: ctx,
          viewport: viewport
        }
        page.render(renderContext)
        this.canvasWrapEle.appendChild(canvas)
      })
    },

    downloadPdf(fileName, postData) {
      return downloadHtmlToPdfApi(postData)
        .then(res => {
          const blob = new Blob([res])
          if ('download' in document.createElement('a')) {
            // 非IE下载
            const elink = document.createElement('a')
            elink.download = fileName
            elink.style.display = 'none'
            elink.href = URL.createObjectURL(blob)
            document.body.appendChild(elink)
            elink.click()
            URL.revokeObjectURL(elink.href) // 释放URL 对象
            document.body.removeChild(elink)
          } else {
            // IE10+下载
            navigator.msSaveBlob(blob, fileName)
          }
          this.closeDialog()
        })
        .finally(() => {
          this.loading = false
        })
    },

    save() {
      changeJxicPurchaseReportApi(this.reportId, {
        reportContent: this.wordHtml
      })
        .then(res => {
          this.$message.success('保存成功')
          this.closeDialog()
        })
        .catch(err => {
          if (err?.message) this.$message.error(err.message)
        })
    }
  }
}
</script>

<style scoped lang="scss">
.chart {
  position: absolute;
  width: 500px;
  height: 300px;
  opacity: 0;
}

.loading {
  height: 200px;
}
</style>
